import { CircleDot, Circle } from 'lucide-react';
import { SliderInput } from './SliderInput';
import { COLORS, SLIDER_CONFIG, MODE_MANAGED, MODE_SELF_DELIVERY } from '@/constants/calculator';

const ACTIVE_ICON_STYLE = { color: COLORS.PURPLE };
const MANAGED_VALUE_STYLE = { color: COLORS.PURPLE };
const SELF_DELIVERY_VALUE_STYLE = { color: COLORS.ORANGE };

const ModeToggle = ({ active, onClick, testId, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="mt-1 transition-transform hover:scale-110 focus:outline-none"
    aria-label={label}
    aria-pressed={active}
    data-testid={testId}
  >
    {active ? (
      <CircleDot className="h-6 w-6" style={ACTIVE_ICON_STYLE} />
    ) : (
      <Circle className="h-6 w-6 text-slate-400" />
    )}
  </button>
);

const RateRow = ({ active, onSelect, toggleTestId, toggleLabel, children }) => (
  <div className="flex items-start gap-4">
    <ModeToggle active={active} onClick={onSelect} testId={toggleTestId} label={toggleLabel} />
    <div className="flex-1">{children}</div>
  </div>
);

export const CostParameters = ({ mode, setMode, values, setters }) => {
  const { managedCost, selfDeliveryRate, holidayPay, supervisorCost, nonProductiveTime, nonEssentialDowntime } = values;
  const {
    setManagedCost, setSelfDeliveryRate, setHolidayPay, setSupervisorCost,
    setNonProductiveTime, setNonEssentialDowntime,
  } = setters;

  const isManaged = mode === MODE_MANAGED;
  const isSelfDelivery = mode === MODE_SELF_DELIVERY;

  return (
    <div className="bg-white border border-slate-200 p-8">
      <div className="mb-6">
        <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 block mb-2">
          COST PARAMETERS
        </label>
        <p className="text-xs text-slate-500">Pick the rate you want to set, the other stays locked.</p>
      </div>

      <RateRow
        active={isManaged}
        onSelect={() => setMode(MODE_MANAGED)}
        toggleTestId="mode-toggle-managed"
        toggleLabel="Use ARLO Managed rate as input"
      >
        <SliderInput
          label="Managed Installation Cost Per Hour"
          value={managedCost}
          onChange={setManagedCost}
          min={SLIDER_CONFIG.MANAGED_COST.min}
          max={SLIDER_CONFIG.MANAGED_COST.max}
          step={SLIDER_CONFIG.MANAGED_COST.step}
          displayValue={`$${managedCost}`}
          minLabel="$50"
          maxLabel="$90"
          testIdPrefix="managed-cost"
          titleSize="text-lg font-semibold"
          valueSize="text-2xl font-black"
          valueStyle={MANAGED_VALUE_STYLE}
          disabled={!isManaged}
          className="mb-6"
        />
      </RateRow>

      <RateRow
        active={isSelfDelivery}
        onSelect={() => setMode(MODE_SELF_DELIVERY)}
        toggleTestId="mode-toggle-self-delivery"
        toggleLabel="Use Self Delivery hourly rate as input"
      >
        <SliderInput
          label="Self Delivery Hourly Rate"
          value={selfDeliveryRate}
          onChange={setSelfDeliveryRate}
          min={SLIDER_CONFIG.SELF_DELIVERY_RATE.min}
          max={SLIDER_CONFIG.SELF_DELIVERY_RATE.max}
          step={SLIDER_CONFIG.SELF_DELIVERY_RATE.step}
          displayValue={`$${selfDeliveryRate}`}
          minLabel="$20"
          maxLabel="$80"
          testIdPrefix="self-delivery-rate"
          titleSize="text-lg font-semibold"
          valueSize="text-2xl font-black"
          valueStyle={SELF_DELIVERY_VALUE_STYLE}
          disabled={!isSelfDelivery}
          className="mb-10"
        />
      </RateRow>

      <SliderInput
        label="Holiday Pay/Sick Pay/ACC/Kiwi Saver"
        description="Set at zero if you have permanent employees or if these costs are all covered by the labour agency."
        value={holidayPay}
        onChange={setHolidayPay}
        min={SLIDER_CONFIG.HOLIDAY_PAY.min}
        max={SLIDER_CONFIG.HOLIDAY_PAY.max}
        step={SLIDER_CONFIG.HOLIDAY_PAY.step}
        displayValue={`${holidayPay}%`}
        minLabel="0%"
        maxLabel="25%"
        testIdPrefix="holiday-pay"
      />

      <SliderInput
        label="Supervisor Cost"
        value={supervisorCost}
        onChange={setSupervisorCost}
        min={SLIDER_CONFIG.SUPERVISOR_COST.min}
        max={SLIDER_CONFIG.SUPERVISOR_COST.max}
        step={SLIDER_CONFIG.SUPERVISOR_COST.step}
        displayValue={`$${supervisorCost}`}
        minLabel="$0"
        maxLabel="$20"
        testIdPrefix="supervisor-cost"
      />

      <SliderInput
        label="Non-Productive Time"
        description="receiving goods, H&S, paid breaks, prestart"
        value={nonProductiveTime}
        onChange={setNonProductiveTime}
        min={SLIDER_CONFIG.NON_PRODUCTIVE.min}
        max={SLIDER_CONFIG.NON_PRODUCTIVE.max}
        step={SLIDER_CONFIG.NON_PRODUCTIVE.step}
        displayValue={`${nonProductiveTime}%`}
        minLabel="0%"
        maxLabel="30%"
        testIdPrefix="non-productive-time"
      />

      <SliderInput
        label="Non-Essential Downtime"
        description="bathroom breaks, smoko breaks etc"
        value={nonEssentialDowntime}
        onChange={setNonEssentialDowntime}
        min={SLIDER_CONFIG.NON_ESSENTIAL.min}
        max={SLIDER_CONFIG.NON_ESSENTIAL.max}
        step={SLIDER_CONFIG.NON_ESSENTIAL.step}
        displayValue={`${nonEssentialDowntime}%`}
        minLabel="0%"
        maxLabel="20%"
        testIdPrefix="non-essential-downtime"
        className="mb-2"
      />
    </div>
  );
};
